import { useQueryClient } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PendingRedirectState,
  useDepositResultNavigation,
} from '@pages/Traders-Room/Deposit/Amount/hooks/useDepositResultNavigation'
import { useThreeDSPostMessage } from '@pages/Traders-Room/Deposit/Amount/hooks/useThreeDSPostMessage'
import {
  hasTokenizedCardParams,
  isThreeDSRequired,
} from '@pages/Traders-Room/Deposit/CardDetails/helpers'
import {
  TokenizedCard,
  tokenizedCardAuthenticationField,
  tokenizedCardIdField,
  tokenizedCardIntentField,
} from '@pages/Traders-Room/Deposit/CardDetails/hooks/useTokenizedCard'
import type { DepositFormValues } from '@pages/Traders-Room/Deposit/DepositFlowLayout/types'
import {
  THREE_DS_FORM_VALUES_STORAGE_KEY,
  THREE_DS_POPUP_WINDOW_NAME,
  THREE_DS_SESSION_ID_STORAGE_KEY,
  THREE_DS_SESSION_RESULT_STORAGE_KEY,
} from '@pages/Traders-Room/Deposit/constants/threeDS'
import {
  createDepositTransaction,
  getCardholderFieldName,
  getDepositErrorMessage,
} from '@pages/Traders-Room/Deposit/helpers'
import { DepositTarget } from '@pages/Traders-Room/Deposit/hooks/types'
import {
  DepositProceedValues,
  useDepositProceedRedirect,
} from '@pages/Traders-Room/Deposit/hooks/useDepositProceedRedirect'
import { CardPaymentProviderProceedModal } from '@pages/Traders-Room/Deposit/modals/CardPaymentProviderProceed.modal'
import { CryptoPaymentProviderProceedModal } from '@pages/Traders-Room/Deposit/modals/CryptoPaymentProviderProceed.modal'
import { LocalInternetBankingProceedModal } from '@pages/Traders-Room/Deposit/modals/LocalInternetBankingProceed.modal'
import { PaymentProviderProceedModal } from '@pages/Traders-Room/Deposit/modals/PaymentProviderProceed.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { denormalizeCreatePspToWalletDepositTransaction } from '@model/CreatePspToWalletDepositTransaction'
import { MasterTransaction, getTransactionQr } from '@model/MasterTransaction'
import {
  PaymentProviderType,
  isLocalInternetBankingProvider,
  isPaymentProviderCardProviderType,
  isPaymentProviderPSPType,
  unionPayPaymentProviderMethodId,
} from '@model/PaymentProviderType'

import { useFundingNav } from '@hooks/useFundingNav'
import { safeLocalStorage } from '@hooks/useStorage'

import { useDepositTracker } from '@analytics/amplitude/deposit'

export const useConfirmDeposit = () => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { email } = useAccountReadContext()
  const isMobile = useWindowResize()
  const locale = useSessionLanguage()
  const queryClient = useQueryClient()
  const { go } = useFundingNav()
  const tracker = useDepositTracker()
  const { callGAEvents } = useGAEvents()
  const proceedModal = useModal(PaymentProviderProceedModal)
  const cardProceedModal = useModal(CardPaymentProviderProceedModal)
  const cryptoProceedModal = useModal(CryptoPaymentProviderProceedModal)
  const localInternetBankingModal = useModal(LocalInternetBankingProceedModal)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [pendingRedirect, setPendingRedirect] = useState<PendingRedirectState | null>(null)

  const { values, validateForm, setSubmitting } = useFormikContext<DepositFormValues>()

  const provider = values.paymentProvider
  const providerCurrencyId = provider?.currency?.id || ''
  const providerCategoryId = provider?.providerCategory?.id ?? PaymentProviderType.None
  const nameOfAccountField = useMemo(() => getCardholderFieldName(provider), [provider])
  const amountValue = Number(values?.amount || 0)
  const derivedTarget: DepositTarget = values?.wallet ? 'wallet' : 'trading-account'
  const providerForm = provider?.parameters?.form ?? []
  const tokenizedEnabled = hasTokenizedCardParams(providerForm)
  const requires3DS = isThreeDSRequired(providerForm)

  const { handleDepositResult } = useDepositResultNavigation({
    values,
    providerCategoryId,
    go,
    setPendingRedirect,
  })

  const startThreeDS = useCallback(
    async (card: TokenizedCard, overrideValues?: DepositFormValues) => {
      const currentValues = overrideValues ?? values
      const tokenId = card.tokenizedCardId
      const isTokenIntent = card.isIntent
      const cardHolderName = currentValues?.fields?.[nameOfAccountField]

      const paymentMethodId = provider?.id
      const currencyId = providerCurrencyId

      const publicApiKey = import.meta.env.VITE_BASIS_THEORY_PUBLIC_KEY
      if (!publicApiKey) {
        setSubmitError(t('Card payment is temporarily unavailable.'))
        return
      }

      console.log({
        tokenId,
        isTokenIntent,
        amountValue,
        currencyId,
        paymentMethodId,
        email,
        cardHolderName,
      })
      if (
        !tokenId ||
        isTokenIntent === undefined ||
        isTokenIntent === null ||
        !amountValue ||
        !currencyId ||
        !paymentMethodId ||
        !email
      ) {
        setSubmitError(t('Something went wrong! Please try again later'))
        return
      }

      if (!cardHolderName) {
        setSubmitError(t('Please enter your cardholder name to continue.'))
        return
      }

      const popup = isMobile
        ? null
        : window.open('about:blank', THREE_DS_POPUP_WINDOW_NAME, 'width=480,height=720')
      const callbackTargetSuffix = popup ? '&target=opener' : ''
      const successUrl = `${window.location.origin}/deposit/3ds/callback?result=success${callbackTargetSuffix}`
      const failureUrl = `${window.location.origin}/deposit/3ds/callback?result=failure${callbackTargetSuffix}`

      try {
        safeLocalStorage.removeItem(THREE_DS_SESSION_ID_STORAGE_KEY)
        safeLocalStorage.removeItem(THREE_DS_SESSION_RESULT_STORAGE_KEY)
        safeLocalStorage.removeItem(THREE_DS_FORM_VALUES_STORAGE_KEY)

        const session = await apiClient.createThreeDSSession({
          tokenId,
          isTokenIntent,
          currencyId,
          amount: amountValue,
          paymentMethodId,
          successUrl,
          failureUrl,
          email,
          cardHolderName,
        })

        safeLocalStorage.setJSON(THREE_DS_FORM_VALUES_STORAGE_KEY, currentValues)
        safeLocalStorage.setItem(THREE_DS_SESSION_ID_STORAGE_KEY, session.id)

        const redirectUrl = `${session.redirectUrl}${publicApiKey}`

        if (isMobile || !popup) {
          window.location.href = redirectUrl
          return
        }

        try {
          popup.location.href = redirectUrl
          popup.focus()
        } catch {
          window.location.href = redirectUrl
        }
      } catch (e) {
        if (popup && !popup.closed) popup.close()
        const errorMessage = getDepositErrorMessage(e)
        tracker.failed({ message: errorMessage })
        setSubmitError(null)
        go(`../transaction/failed?3ds=failure&reason=${encodeURIComponent(errorMessage)}`, {
          replace: true,
          state: { reason: errorMessage },
        })
        throw e
      }
    },
    [
      amountValue,
      apiClient,
      email,
      go,
      isMobile,
      nameOfAccountField,
      provider?.id,
      providerCurrencyId,
      t,
      tracker,
      values,
    ]
  )

  const handleConfirm = useCallback(
    async (card?: TokenizedCard) => {
      setSubmitError(null)

      const errors = await validateForm()
      if (Object.keys(errors).length > 0) return

      const mergedValues =
        tokenizedEnabled && card
          ? {
              ...values,
              fields: {
                ...values.fields,
                [tokenizedCardIdField]: card.tokenizedCardId,
                [tokenizedCardIntentField]: card.isIntent ? 'true' : 'false',
                [tokenizedCardAuthenticationField]: card.authentication ?? '',
              },
            }
          : values

      if (tokenizedEnabled && card) {
        const shouldForceThreeDS = requires3DS || card.authentication === 'sca_required'
        if (shouldForceThreeDS) {
          setSubmitting(true)
          callGAEvents({
            deposit_amount: Number(mergedValues?.amount || 0),
            deposit_currency:
              mergedValues?.paymentProvider?.currency?.id ||
              mergedValues?.wallet?.currency?.id ||
              mergedValues?.tradingAccount?.currency?.id ||
              '',
            deposit_provider: mergedValues?.paymentProvider?.name || '',
            deposit_target: derivedTarget || '',
          }).Deposit.Funnel.submitted()
          try {
            await startThreeDS(card, mergedValues)
          } catch {
            setSubmitting(false)
          }
          return
        }
      }

      setSubmitting(true)
      try {
        const tx = await apiClient.transactionDeposit(
          denormalizeCreatePspToWalletDepositTransaction(
            createDepositTransaction(mergedValues, derivedTarget),
            locale
          )
        )

        tracker.submitted({ deposit_id: tx.id })
        queryClient.invalidateQueries({ queryKey: ['wallet'] })
        queryClient.invalidateQueries({ queryKey: ['transaction'] })
        queryClient.invalidateQueries({ queryKey: ['tradingAccount'] })
        handleDepositResult(tx, mergedValues)
      } catch (error: unknown) {
        tracker.failed({ message: getDepositErrorMessage(error) })
        setSubmitError(t('Something went wrong! Please try again later'))
        setSubmitting(false)
      }
    },
    [
      apiClient,
      callGAEvents,
      derivedTarget,
      handleDepositResult,
      locale,
      queryClient,
      requires3DS,
      setSubmitting,
      startThreeDS,
      t,
      tracker,
      tokenizedEnabled,
      validateForm,
      values,
    ]
  )

  useEffect(() => {
    setSubmitting(false)
  }, [setSubmitting])

  const proceedToRedirect = useCallback(() => {
    if (!pendingRedirect) return

    go(`../redirect`, {
      state: {
        transaction: pendingRedirect.tx,
        values: pendingRedirect.values,
      },
    })

    setPendingRedirect(null)
  }, [go, pendingRedirect])

  const pendingProceedValues = useMemo<DepositProceedValues | undefined>(() => {
    if (!pendingRedirect) return undefined
    return toProceedValues(pendingRedirect.values)
  }, [pendingRedirect])

  const pendingProceedQr = useMemo(
    () => (pendingRedirect ? getTransactionQr(pendingRedirect.tx) : undefined),
    [pendingRedirect]
  )

  const handleQrSuccess = useCallback(() => {
    if (!pendingRedirect) return
    go(`../transaction/succeed`, {
      state: { data: pendingRedirect.tx, providerCategoryId },
    })
    setPendingRedirect(null)
  }, [go, pendingRedirect, providerCategoryId])

  const handleQrDeclined = useCallback(() => {
    if (!pendingRedirect) return
    go(`../transaction/failed`, {
      replace: true,
      state: { data: pendingRedirect.tx, providerCategoryId },
    })
    setPendingRedirect(null)
  }, [go, pendingRedirect, providerCategoryId])

  useDepositProceedRedirect({
    pendingRedirectTransactionId: pendingRedirect?.id,
    pendingRedirectValues: pendingProceedValues,
    pendingRedirectQr: pendingProceedQr,
    transactionId: pendingRedirect?.tx?.id,
    onProceedRedirect: proceedToRedirect,
    onCloseModal: () => setSubmitting(false),
    onQrSuccess: handleQrSuccess,
    onQrDeclined: handleQrDeclined,

    openCardModal: (p) => cardProceedModal.open(p),
    openCryptoModal: (p) => cryptoProceedModal.open(p),
    openPaymentModal: (p) => proceedModal.open(p),
    openLocalInternetBankingModal: (p) => localInternetBankingModal.open(p),

    shouldOpenCardModal: (categoryId, providerId) =>
      isPaymentProviderCardProviderType(categoryId) ||
      providerId === unionPayPaymentProviderMethodId,
    shouldOpenPaymentModal: (categoryId) => isPaymentProviderPSPType(categoryId),

    isLocalInternetBankingProvider: (v) => isLocalInternetBankingProvider(v.paymentProvider),
    getLocalInternetBankingProviderName: (v) =>
      v.paymentProvider?.description || t('Local Internet Banking'),
  })

  useThreeDSPostMessage({
    onDepositResult: (tx: MasterTransaction, txValues: any) => {
      tracker.submitted({ deposit_id: tx.id })
      handleDepositResult(tx, txValues)
    },
    onAuthFailed: (reason?: string) => {
      tracker.failed({ message: getDepositErrorMessage(reason) })
      go(`../transaction/failed?3ds=failure&reason=${encodeURIComponent(reason || '')}`, {
        replace: true,
        state: { reason },
      })
    },
    onDepositFailed: (reason?: string) => {
      tracker.failed({ message: getDepositErrorMessage(reason) })
      setSubmitError(null)
      go(`../transaction/failed`, { replace: true })
    },
  })

  return { handleConfirm, submitError }
}

const toProceedValues = (v: DepositFormValues): DepositProceedValues => {
  const raw: unknown = v?.paymentProvider?.providerCategory?.id

  const categoryId =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? (() => {
          const s = raw.trim()
          if (!s) return undefined
          const n = Number(s)
          return Number.isFinite(n) ? n : undefined
        })()
      : undefined

  return {
    amount: v.amount,
    fields: v.fields ?? {},
    paymentProvider: {
      id: v.paymentProvider?.id,
      name: v.paymentProvider?.name,
      description: v.paymentProvider?.description,
      providerCategory: { id: categoryId },
    },
    wallet: v.wallet?.currency?.id ? { currency: { id: v.wallet.currency.id } } : undefined,
    tradingAccount: v.tradingAccount?.currency?.id
      ? { currency: { id: v.tradingAccount.currency.id } }
      : undefined,
  }
}
